import { MutableRefObject } from 'react';
import Handsontable from 'handsontable/base';
import { getFirstSelection } from '../../selectionUtils/tableSelectionAccessors';
import {
    isColInSelection,
    isRowInSelection,
    isSelectingSingleSelection,
    isSelectingSingleSelectionAndCell,
} from '../../selectionUtils/tableSelectionPredicateUtils';

/**
 * Hide/show "current" borders based on whether only single cell is selection.
 * This is to replicate the behaviour of Numbers app, where "current" borders
 * will not be shown when there are a cell range selected.
 */
export const fixSelectionBorderDisplay = (hot: Handsontable): void => {
    const cellSelections = hot.getSelected();
    if (!cellSelections) return;

    const singleCellSelected = cellSelections && isSelectingSingleSelectionAndCell(cellSelections);
    const currentSelectionBorderElements = hot.rootElement.querySelectorAll('.ht_master .htBorders .wtBorder.current');
    const currentIsHidden = currentSelectionBorderElements[0]?.classList.contains('hidden');

    if ((!singleCellSelected && !currentIsHidden) || (singleCellSelected && currentIsHidden)) {
        currentSelectionBorderElements.forEach((el) => {
            el.classList.toggle('hidden', !singleCellSelected);
        });
    }
};

/**
 * Repositions the autofill handle when the active selection touches the table edges.
 */
export const repositionAutofillHandle = (
    hot: Handsontable | null,
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>,
) => {
    const currentSelections = hot?.getSelected();
    if (!currentSelections || !isSelectingSingleSelection(currentSelections)) return;

    const currentSelection = getFirstSelection(currentSelections);
    if (!currentSelection) return;

    requestAnimationFrame(() => {
        if (!hot || !hotTableContainerRef.current) return;

        const finalRowSelected = isRowInSelection(hot.countRows() - 1, currentSelection);
        const finalColSelected = isColInSelection(hot.countCols() - 1, currentSelection);
        const shouldMoveAutofillHandle = finalRowSelected && finalColSelected;

        if (!shouldMoveAutofillHandle) {
            hotTableContainerRef.current.style.setProperty('--autofill-handle-position-offset-x', '0px');
            hotTableContainerRef.current.style.setProperty('--autofill-handle-position-offset-y', '0px');
            return;
        }

        const singleCellSelected = isSelectingSingleSelectionAndCell(currentSelections);
        const selector = `.ht_master .wtBorder.${singleCellSelected ? 'current' : 'area'}:not(.hidden)`;
        const borders = hot.rootElement.querySelectorAll(selector) as NodeListOf<HTMLElement>;
        const [topBorder, leftBorder] = borders;

        if (!topBorder || !leftBorder) return;

        const handleWidthOffsetX = isColInSelection(0, currentSelection) ? 5 : 3;
        const handleWidthOffsetY = isRowInSelection(0, currentSelection) ? 5 : 3;

        const distanceX = topBorder.offsetWidth - handleWidthOffsetX;
        const distanceY = leftBorder.offsetHeight - handleWidthOffsetY;

        hotTableContainerRef.current.style.setProperty('--autofill-handle-position-offset-x', `-${distanceX}px`);
        hotTableContainerRef.current.style.setProperty('--autofill-handle-position-offset-y', `-${distanceY}px`);
    });
};
